import { z } from "zod"

export const galleryStudioFormatSchema = z.enum(["square", "portrait", "landscape"])
export type GalleryStudioFormat = z.infer<typeof galleryStudioFormatSchema>

export const GALLERY_STUDIO_INTENT_TARGET_LIMIT = 500

export const createGalleryStudioIntentFileSchema = z
  .object({
    targetSimpleSkus: z
      .array(z.string().trim().min(1).max(256))
      .min(1)
      .max(GALLERY_STUDIO_INTENT_TARGET_LIMIT),
  })
  .strict()
export type CreateGalleryStudioIntentFile = z.infer<typeof createGalleryStudioIntentFileSchema>

export const galleryStudioSlotNameSchema = z.string().trim().min(1).max(160)
export const galleryStudioSlotPlanFileSchema = z
  .object({
    expectedVersion: z.number().int().positive(),
    secondarySlots: z
      .array(
        z
          .object({
            id: z.string().uuid(),
            name: galleryStudioSlotNameSchema,
          })
          .strict()
      )
      .max(20),
  })
  .strict()
export type GalleryStudioSlotPlanFile = z.infer<typeof galleryStudioSlotPlanFileSchema>

export const galleryStudioIntentSlotSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(["main", "secondary"]),
  position: z.number().nullable(),
  isLocked: z.boolean(),
  isActive: z.boolean(),
})
export type GalleryStudioIntentSlot = z.infer<typeof galleryStudioIntentSlotSchema>

export const galleryStudioSlotPlanResultSchema = z.object({
  version: z.number().int().positive(),
  slots: z.array(galleryStudioIntentSlotSchema),
})
export type GalleryStudioSlotPlanResult = z.infer<typeof galleryStudioSlotPlanResultSchema>

export const galleryStudioSlotPlanConflictDetailsSchema = z.object({
  currentVersion: z.number().int().positive(),
})

export const galleryStudioBootstrapInputsSchema = z.object({
  configSku: z.string(),
  store: z.string(),
  candidates: z.array(
    z.object({
      simpleSku: z.string(),
      flavor: z.string().nullable(),
      size: z.string().nullable(),
    })
  ),
})
export type GalleryStudioBootstrapInputs = z.infer<typeof galleryStudioBootstrapInputsSchema>

const copySchema = z.object({
  headline: z.string(),
  subheadline: z.string(),
  benefit: z.string(),
  trust: z.string(),
})

export const galleryStudioPlanSlotSchema = z.object({
  slotId: z.string().uuid(),
  name: z.string(),
  brief: z.string(),
  copy: copySchema,
  visualDirectionReferenceAssetIds: z.array(z.string().uuid()),
  marketplaces: z.array(z.string()),
  formats: z.array(galleryStudioFormatSchema),
  simpleSkus: z.array(z.string()),
  masterSimpleSku: z.string().optional(),
})
export type GalleryStudioPlanSlot = z.infer<typeof galleryStudioPlanSlotSchema>

export const galleryStudioCandidateSchema = z.object({
  id: z.string(),
  revisionId: z.string(),
  url: z.string(),
  width: z.number(),
  height: z.number(),
  format: z.string(),
  createdAt: z.string(),
})
export type GalleryStudioCandidate = z.infer<typeof galleryStudioCandidateSchema>

export const galleryStudioCandidateHistoryPageSchema = z.object({
  candidates: z.array(galleryStudioCandidateSchema),
  nextCursor: z.string().nullable(),
})
export type GalleryStudioCandidateHistoryPage = z.infer<
  typeof galleryStudioCandidateHistoryPageSchema
>

const galleryStudioVisualDirectionStateSchema = z
  .object({
    selection: z
      .object({
        selection: z.object({ versionId: z.string().uuid().nullable() }).passthrough(),
        version: z.object({ id: z.string().uuid() }).passthrough().nullable(),
      })
      .passthrough()
      .nullable(),
  })
  .passthrough()

const galleryIdentityContractSchema = z.object({
  id: z.string().uuid(),
  simpleSku: z.string(),
  sourceUrl: z.string().url(),
  sourceArtifactHash: z.string(),
  sourceWidth: z.number(),
  sourceHeight: z.number(),
  status: z.enum(["awaiting_anchor", "approved", "stale"]),
  approvedAnchorAttemptId: z.string().uuid().nullable(),
  rules: z.record(z.string(), z.unknown()),
})

const galleryIdentityAnchorAttemptSchema = z.object({
  id: z.string().uuid(),
  identityContractId: z.string().uuid(),
  status: z.enum(["queued", "running", "completed", "approved", "rejected", "failed"]),
  url: z.string().url().nullable(),
  artifactHash: z.string().nullable(),
})

const galleryFamilyMasterSchema = z.object({
  id: z.string().uuid(),
  slotId: z.string().uuid(),
  marketplace: z.string(),
  format: galleryStudioFormatSchema,
  simpleSku: z.string(),
  plateId: z.string().uuid(),
  plateUrl: z.string().url(),
  candidateId: z.string().uuid(),
  candidateUrl: z.string().url(),
  status: z.enum(["approved", "stale"]),
})

const boundedHistoryPageSchema = z.object({
  nextCursor: z.string().uuid().nullable(),
  limit: z.number().int().positive(),
})

export const galleryStudioPlanInputsSchema = z.object({
  configSku: z.string(),
  store: z.string(),
  intent: z.object({
    id: z.string(),
    targetSimpleSkus: z.array(z.string()),
    slotPlanVersion: z.number(),
    slots: z.array(galleryStudioIntentSlotSchema),
  }),
  approvalStates: z.array(
    z.object({
      id: z.string(),
      intentId: z.string(),
      simpleSku: z.string(),
      marketplace: z.string(),
      format: galleryStudioFormatSchema,
      currentApprovalId: z.string(),
      isStale: z.boolean(),
      staleReason: z.string().nullable(),
      projectionStatus: z.enum(["pending", "projected", "failed"]),
      projectionError: z.string().nullable(),
      projectedAt: z.string().nullable(),
      updatedAt: z.string(),
    })
  ),
  productFacts: z.array(
    z.object({
      simpleSku: z.string(),
      configSku: z.string().nullable(),
      name: z.string().nullable(),
      brand: z.string().nullable(),
      flavor: z.string().nullable(),
      size: z.string().nullable(),
      ingredients: z.string().nullable(),
      categoryPath: z.string().nullable(),
      masterCategory: z.string().nullable(),
    })
  ),
  marketplaces: z.array(z.string()),
  marketplaceContracts: z.array(
    z.object({ marketplace: z.string(), format: galleryStudioFormatSchema }).passthrough()
  ),
  currentIdentityContracts: z.array(galleryIdentityContractSchema),
  currentIdentityAnchorAttempts: z.array(galleryIdentityAnchorAttemptSchema),
  currentFamilyMasters: z.array(galleryFamilyMasterSchema),
  identityContracts: z.array(galleryIdentityContractSchema),
  identityContractsPage: boundedHistoryPageSchema,
  identityAnchorAttempts: z.array(galleryIdentityAnchorAttemptSchema),
  identityAnchorAttemptsPage: boundedHistoryPageSchema,
  familyMasters: z.array(galleryFamilyMasterSchema),
  familyMastersPage: boundedHistoryPageSchema,
  generationReferences: z.array(
    z.object({
      id: z.string(),
      versionId: z.string(),
      name: z.string(),
      kind: z.string(),
      mediaType: z.string(),
      byteHash: z.string(),
    })
  ),
  visualDirection: galleryStudioVisualDirectionStateSchema.nullable(),
  latestPlan: z
    .object({
      id: z.string(),
      version: z.number(),
      secondarySlots: z.array(galleryStudioPlanSlotSchema),
      createdBy: z.string(),
      createdAt: z.string(),
    })
    .nullable(),
  existingDrafts: z.array(
    z.object({
      id: z.string(),
      slotId: z.string(),
      marketplace: z.string(),
      format: galleryStudioFormatSchema,
      simpleSku: z.string(),
      revisionId: z.string().nullable(),
      candidates: z.array(galleryStudioCandidateSchema),
      candidateHistoryPage: z.object({
        nextCursor: z.string().nullable(),
        limit: z.number(),
      }),
    })
  ),
  existingDraftsPage: z.object({
    nextCursor: z.string().nullable(),
    limit: z.number(),
  }),
})
export type GalleryStudioPlanInputs = z.infer<typeof galleryStudioPlanInputsSchema>

export function isGalleryStudioPlanSlotReady(
  slot: GalleryStudioPlanSlot,
  inputs: GalleryStudioPlanInputs
): boolean {
  const copyReady = Object.values(slot.copy).every((value) => value.trim().length > 0)
  const selectedVisualDirectionVersionId = inputs.visualDirection?.selection?.version?.id ?? null
  const identitySkus = new Set(
    inputs.currentIdentityContracts
      .filter((contract) => contract.status === "approved")
      .map((contract) => contract.simpleSku)
  )
  const referenceIds = new Set(
    inputs.generationReferences
      .filter((reference) => reference.versionId === selectedVisualDirectionVersionId)
      .map((reference) => reference.id)
  )
  const contracts = new Set(
    inputs.marketplaceContracts.map((contract) => `${contract.marketplace}|${contract.format}`)
  )
  return (
    slot.brief.trim().length > 0 &&
    copyReady &&
    slot.simpleSkus.length > 0 &&
    slot.marketplaces.length > 0 &&
    slot.formats.length > 0 &&
    slot.masterSimpleSku !== undefined &&
    slot.simpleSkus.includes(slot.masterSimpleSku) &&
    selectedVisualDirectionVersionId !== null &&
    slot.simpleSkus.every((simpleSku) => identitySkus.has(simpleSku)) &&
    slot.visualDirectionReferenceAssetIds.every((id) => referenceIds.has(id)) &&
    slot.marketplaces.every((marketplace) =>
      slot.formats.every((format) => contracts.has(`${marketplace}|${format}`))
    )
  )
}

export const galleryDraftIdentitySchema = z.object({
  slotId: z.string(),
  simpleSku: z.string(),
  marketplace: z.string(),
  format: galleryStudioFormatSchema,
})
export type GalleryDraftIdentity = z.infer<typeof galleryDraftIdentitySchema>

const galleryDraftGenerationSettingsSchema = z.object({
  width: z.number(),
  height: z.number(),
  quality: z.enum(["low", "medium", "high"]),
  outputMediaType: z.literal("image/png"),
})

const galleryDraftFrozenSceneRequestSchema = z
  .object({
    id: z.string(),
    slotBrief: z.string(),
    targetContract: z.record(z.string(), z.unknown()),
    productFacts: z.record(z.string(), z.unknown()),
    productFactsHash: z.string(),
    presetId: z.string(),
    presetVersion: z.number(),
    presetDirection: z.string(),
    compiledPrompt: z.string(),
    promptVersion: z.string(),
    visualDirectionVersionId: z.string().nullable(),
    identityContractId: z.string().uuid(),
    anchorAttemptId: z.string().uuid(),
    familyMasterId: z.string().uuid().nullable(),
    sceneRole: z.enum(["master", "sibling"]),
    referencePins: z.array(
      z.object({
        id: z.string(),
        versionId: z.string(),
        kind: z.enum(["style_image", "model_reference"]),
        mediaType: z.string(),
        byteHash: z.string(),
      })
    ),
    provider: z.string(),
    model: z.string(),
    settings: z.record(z.string(), z.unknown()),
    generationInputFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .passthrough()

const galleryDraftReviewedItemSchema = galleryDraftIdentitySchema.extend({
  itemKey: z.string(),
  slotName: z.string(),
  slotPosition: z.number(),
  slotPlanVersion: z.number(),
  brief: z.string(),
  copy: copySchema,
  visualDirectionReferenceAssetIds: z.array(z.string()),
  generationProvider: z.string(),
  generationModel: z.string(),
  generationSettings: galleryDraftGenerationSettingsSchema,
  generationInputFingerprint: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  assemblyInputFingerprint: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable()
    .default(null),
  renderer: z.object({ identity: z.string(), version: z.string() }),
  font: z.object({ identity: z.string(), hash: z.string() }),
  regenerateExisting: z.boolean(),
  targetSimpleSkus: z.array(z.string()),
  expectedParentRevisionId: z.string().nullable().optional(),
  nextStage: z.enum(["paid_image", "analysis", "assembly"]),
  sceneRole: z.enum(["master", "sibling"]).default("master"),
  identityContractId: z.string().uuid().nullable().default(null),
  anchorAttemptId: z.string().uuid().nullable().default(null),
  familyMasterId: z.string().uuid().nullable().default(null),
  reuseSceneRequestId: z.string().nullable().default(null),
  sceneRequest: galleryDraftFrozenSceneRequestSchema.nullable().default(null),
  reuseBackgroundRequestId: z.string().nullable().optional(),
  backgroundRequest: galleryDraftFrozenSceneRequestSchema.nullable().optional(),
  cutout: z
    .object({ id: z.string(), artifactHash: z.string(), inputFingerprint: z.string() })
    .nullable()
    .optional(),
  composition: z.object({
    versionId: z.string().nullable(),
    colors: z.object({ foreground: z.string(), background: z.string(), accent: z.string() }),
    assets: z.array(z.object({ id: z.string(), byteHash: z.string(), kind: z.string() })),
  }),
})

export const galleryDraftPreviewSchema = z.object({
  previewId: z.string().uuid(),
  previewHash: z.string().regex(/^[a-f0-9]{64}$/),
  planId: z.string(),
  planVersion: z.number(),
  totalItems: z.number(),
  paidImageCallCount: z.number(),
  missingAnchorCount: z.number(),
  masterSceneCallCount: z.number(),
  siblingSceneCallCount: z.number(),
  analysisCallCount: z.number(),
  assemblyOnlyCount: z.number(),
  skippedExistingDrafts: z.number(),
  mainSkipped: z.boolean(),
  exceedsPaidItemCap: z.boolean(),
  missingPrerequisites: z.array(
    z.object({ itemKey: z.string(), code: z.string(), message: z.string() })
  ),
  items: z.array(
    galleryDraftReviewedItemSchema.extend({
      status: z.enum(["ready", "blocked", "skip_existing"]),
      skippedExistingDraft: z.boolean(),
      retryAvailable: z.boolean(),
      priorAttempted: z.boolean(),
      regenerateExisting: z.boolean(),
      requiresPaidCall: z.boolean(),
      blockers: z.array(z.object({ code: z.string(), message: z.string() })),
    })
  ),
})
export type GalleryDraftPreview = z.infer<typeof galleryDraftPreviewSchema>

export const galleryStudioPaidWorkControlSchema = z.object({
  enabled: z.boolean(),
  updatedBy: z.string(),
  updatedAt: z.string(),
})
export type GalleryStudioPaidWorkControl = z.infer<typeof galleryStudioPaidWorkControlSchema>

export const galleryDraftJobSchema = z
  .object({
    id: z.string(),
    previewId: z.string(),
    status: z.enum([
      "queued",
      "running",
      "completed",
      "partial",
      "failed",
      "cancelling",
      "cancelled",
    ]),
    planVersion: z.number(),
    paidImageCallCount: z.number(),
    analysisCallCount: z.number(),
    frozenWorklist: z.array(galleryDraftReviewedItemSchema),
    cancelRequested: z.boolean(),
    terminalMessage: z.string().nullable(),
    analysisEvidencePage: z.object({
      limit: z.number(),
      truncated: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    outcome: z.object({
      completed: z.number(),
      failed: z.number(),
      stoppedBeforeSpend: z.number(),
      skippedExisting: z.number(),
      completedBeforeCancel: z.number(),
      paidCallsStarted: z.number(),
      partialSuccess: z.boolean(),
      cancellationRequested: z.boolean(),
    }),
    items: z.array(
      galleryDraftIdentitySchema.extend({
        id: z.string(),
        itemKey: z.string(),
        slotName: z.string(),
        status: z.enum([
          "queued",
          "running",
          "completed",
          "failed",
          "skipped_existing",
          "stopped_before_spend",
        ]),
        stage: z.string(),
        failureCode: z.string().nullable(),
        failureMessage: z.string().nullable(),
        revisionId: z.string().nullable(),
        revisionHash: z
          .string()
          .regex(/^[a-f0-9]{64}$/)
          .nullable(),
        candidateId: z.string().nullable(),
        candidateArtifactHash: z
          .string()
          .regex(/^[a-f0-9]{64}$/)
          .nullable(),
        candidateUrl: z.string().url().nullable(),
        sceneRequestId: z.string().uuid().nullable(),
        scenePlateId: z.string().uuid().nullable(),
        reviewEvidence: z
          .object({
            sourceUrl: z.string().url(),
            sceneUrl: z.string().url(),
            productCrop: z
              .object({
                x: z.number().min(0).max(1),
                y: z.number().min(0).max(1),
                width: z.number().positive().max(1),
                height: z.number().positive().max(1),
              })
              .nullable(),
            plateStatus: z.string(),
            rejectionClass: z.string().nullable(),
            usabilityReasons: z.array(z.string()).max(64),
          })
          .nullable(),
        requiresPaidCall: z.boolean(),
        completedBeforeCancel: z.boolean(),
        imageProviderEvidence: z
          .object({
            provider: z.string(),
            model: z.string(),
            status: z.string(),
            clientRequestId: z.string().nullable(),
            providerRequestId: z.string().nullable(),
            responseProvenance: z.record(z.string(), z.unknown()).nullable(),
          })
          .nullable(),
        imageEvidenceSource: z.enum(["current_job", "reused_request"]).nullable(),
        analysisProviderEvidence: z.array(
          z.object({
            id: z.string(),
            attempt: z.number(),
            status: z.string(),
            provider: z.string(),
            model: z.string(),
            clientRequestId: z.string(),
            providerRequestId: z.string().nullable(),
            rawProvenance: z.record(z.string(), z.unknown()),
            terminalCode: z.string().nullable(),
            terminalMessage: z.string().nullable(),
            providerStartedAt: z.string().nullable(),
            normalizedResult: z
              .object({
                fidelityFindings: z.array(
                  z.object({
                    code: z.string(),
                    confidence: z.number(),
                    detail: z.string(),
                    expected: z.string().nullable(),
                    observed: z.string().nullable(),
                  })
                ),
                productCrop: z
                  .object({
                    x: z.number(),
                    y: z.number(),
                    width: z.number(),
                    height: z.number(),
                  })
                  .optional(),
              })
              .passthrough()
              .nullable(),
          })
        ),
      })
    ),
  })
  .passthrough()
export type GalleryDraftJob = z.infer<typeof galleryDraftJobSchema>
